import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Popconfirm,
  Tag,
  Input,
  Select,
  Form,
  Drawer,
  Row,
  Col,
  Statistic,
  Upload,
  Image,
  Divider,
  Modal,
  Avatar,
  Tabs,
  InputNumber,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  UploadOutlined,
  EyeOutlined,
  ScanOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import useStore from '../store/useStore';
import { productsAPI } from '../services/productsApi';
import { uploadAPI } from '../services/uploadApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';

import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;


const Products = () => {
  const { products, setProducts, categories } = useStore();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Orders and purchase orders will be fetched from backend when needed
  const [orders] = useState([]);
  const [purchaseOrders] = useState([]);
  
  // View states
  const [currentView, setCurrentView] = useState('list'); // 'list', 'detail'
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Drawer states
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [form] = Form.useForm();
  const [imageFileList, setImageFileList] = useState([]);



  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (err) {
      // If backend is not reachable or tenant header missing, show empty list instead of error screen
      setProducts([]);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [setProducts]);

  // All data now comes from backend APIs - no mock data

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchText.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchText.toLowerCase())
  );

  // View handlers
  const handleRowClick = (product) => {
    setSelectedProduct(product);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedProduct(null);
  };

  // Drawer handlers
  const handleAddProduct = () => {
    setEditingProduct(null);
    form.resetFields();
    setImageFileList([]);
    // initialize images array
    form.setFieldsValue({ images: [] });
    setDrawerVisible(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      ...product,
      categoryId: product.category?.id,
      images: product.images || (product.image ? [product.image] : []),
    });
    // Initialize file list from existing images (or legacy single image)
    const imgs = product.images || (product.image ? [product.image] : []);
    if (imgs && imgs.length) {
      setImageFileList(
        imgs.map((url, idx) => {
          // Handle both server URLs and base64 images
          let displayUrl = url;
          let serverUrl = url;
          
          if (url.startsWith('/uploads/')) {
            // Server URL - make it absolute for display
            displayUrl = `http://localhost:3001${url}`;
            serverUrl = url;
          } else if (url.startsWith('data:image/')) {
            // Base64 image - use as is for display, no server URL
            displayUrl = url;
            serverUrl = null;
          }
          
          return {
            uid: `-init-${idx}`,
            name: `image-${idx + 1}`,
            status: 'done',
            url: displayUrl,
            serverUrl: serverUrl,
          };
        })
      );
    } else {
      setImageFileList([]);
    }
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingProduct(null);
    form.resetFields();
    setImageFileList([]);
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      // Coerce legacy single image to images array
      let payload = { ...values };
      // Trim critical string fields
      if (typeof payload.name === 'string') payload.name = payload.name.trim();
      if (typeof payload.sku === 'string') payload.sku = payload.sku.trim();
      if (!payload.images && payload.image) {
        payload.images = [payload.image];
        delete payload.image;
      }
      
      // Handle images: use server URLs from fileList instead of form field
      if (imageFileList && imageFileList.length > 0) {
        // Get server URLs from fileList (excludes base64 images)
        payload.images = imageFileList
          .map(file => file.serverUrl)
          .filter(Boolean); // Remove null/undefined values
      } else {
        payload.images = [];
      }
      // Enrich with categoryName for backend/list display if categoryId chosen
      if (payload.categoryId && !payload.categoryName) {
        const cat = categories.find((c) => c.id === payload.categoryId);
        if (cat?.name) payload.categoryName = cat.name;
      }
      
      // Payload ready for API call
      
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, payload);
        message.success('Product updated successfully');
      } else {
        await productsAPI.create(payload);
        message.success('Product created successfully');
      }

      await fetchProducts();
      handleDrawerClose();
    } catch (err) {
      // Surface backend validation or duplicate errors clearly
      const msg = err?.message || 'Failed to save product';
      message.error(msg);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await productsAPI.delete(productId);
      await fetchProducts();
      message.success('Product deleted successfully');
      
      // If we're viewing the deleted product, go back to list
      if (selectedProduct?.id === productId) {
        handleBackToList();
      }
    } catch (err) {
      message.error('Failed to delete product');
    }
  };

  const getStockStatus = (currentStock, reorderPoint) => {
    if (currentStock <= 0) return { color: 'red', text: 'Out of Stock' };
    if (currentStock <= reorderPoint) return { color: 'orange', text: 'Low Stock' };
    return { color: 'green', text: 'In Stock' };
  };

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={record.image || record.images?.[0]}
            icon={<EyeOutlined />}
            size="large"
            shape="square"
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-sm text-gray-500">SKU: {record.sku}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (category) => category || 'Uncategorized',
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
      sorter: (a, b) => a.currentStock - b.currentStock,
      render: (stock, record) => {
        const status = getStockStatus(stock, record.reorderPoint);
        return (
          <div>
            <div className="font-medium">{stock}</div>
            <Tag color={status.color} size="small">
              {status.text}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price - b.price,
      render: (price) => `$${price?.toFixed(2)}`,
    },
    {
      title: 'Reorder Point',
      dataIndex: 'reorderPoint',
      key: 'reorderPoint',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditProduct(record);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              Modal.confirm({
                title: 'Delete Product',
                content: 'Are you sure you want to delete this product?',
                onOk: () => handleDeleteProduct(record.id),
              });
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },

  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading products..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchProducts}
        title="Failed to load products"
      />
    );
  }

  // Render product detail view
  const renderProductDetail = () => {
    if (!selectedProduct) return null;

    const stockStatus = getStockStatus(selectedProduct.currentStock, selectedProduct.reorderPoint);

    // Build Order History rows (per order item containing this product)
    const orderItemRows = orders.flatMap((order) => {
      const matches = (order.items || []).filter((it) => it.productId === selectedProduct.id);
      return matches.map((it) => ({
        key: `${order.id}-${it.productId}`,
        orderId: order.id,
        orderDate: order.orderDate,
        customerName: order.customerName,
        quantity: it.quantity,
        unitPrice: it.price,
        lineTotal: (it.quantity || 0) * (it.price || 0),
        status: order.status,
      }));
    });

    const orderHistoryColumns = [
      { title: 'Order #', dataIndex: 'orderId', key: 'orderId' },
      { title: 'Date', dataIndex: 'orderDate', key: 'orderDate', render: (d) => new Date(d).toLocaleDateString() },
      { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
      { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
      { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', render: (v) => `$${v?.toFixed(2)}` },
      { title: 'Subtotal', dataIndex: 'lineTotal', key: 'lineTotal', render: (v) => `$${v?.toFixed(2)}` },
      { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color="blue">{s}</Tag> },
    ];

    // Build Product Vendors table by scanning purchase orders for this product
    const vendorAgg = {};
    purchaseOrders.forEach((po) => {
      (po.items || []).forEach((it) => {
        if (it.productId === selectedProduct.id) {
          const key = po.vendorId || po.vendorName;
          if (!vendorAgg[key]) {
            vendorAgg[key] = {
              key,
              vendorId: po.vendorId,
              vendorName: po.vendorName,
              totalQty: 0,
              lastOrdered: po.orderDate,
              totalSpent: 0,
            };
          }
          vendorAgg[key].totalQty += it.quantity || 0;
          vendorAgg[key].totalSpent += (it.quantity || 0) * (it.unitPrice || 0);
          if (po.orderDate && (!vendorAgg[key].lastOrdered || new Date(po.orderDate) > new Date(vendorAgg[key].lastOrdered))) {
            vendorAgg[key].lastOrdered = po.orderDate;
          }
        }
      });
    });
    const vendorRows = Object.values(vendorAgg);
    const vendorColumns = [
      { title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName' },
      { title: 'Total Purchased', dataIndex: 'totalQty', key: 'totalQty' },
      { title: 'Total Spent', dataIndex: 'totalSpent', key: 'totalSpent', render: (v) => `$${v.toFixed(2)}` },
      { title: 'Last Ordered', dataIndex: 'lastOrdered', key: 'lastOrdered', render: (d) => d ? new Date(d).toLocaleDateString() : '-' },
    ];

    return (
      <DetailView
        title={selectedProduct.name}
        subtitle={`SKU: ${selectedProduct.sku}`}
        onBack={handleBackToList}
        onEdit={() => handleEditProduct(selectedProduct)}
        onDelete={() => {
          Modal.confirm({
            title: 'Delete Product',
            content: 'Are you sure you want to delete this product?',
            onOk: () => handleDeleteProduct(selectedProduct.id),
          });
        }}
        tags={[
          { label: stockStatus.text, color: stockStatus.color }
        ]}
        actions={[
          {
            label: 'Generate Barcode',
            icon: <ScanOutlined />,
            onClick: () => message.info('Barcode generation feature coming soon')
          },
          {
            label: 'Export',
            icon: <ExportOutlined />,
            onClick: () => message.info('Export feature coming soon')
          }
        ]}
      >
        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Row gutter={24}>
                  <Col span={16}>
                    <DetailSection title="Product Information">
                      <Row gutter={16}>
                        <Col span={12}>
                          <DetailField label="Product Name" value={selectedProduct.name} />
                          <DetailField label="SKU" value={selectedProduct.sku} />
                          <DetailField label="Category" value={selectedProduct.categoryName || 'Uncategorized'} />
                        </Col>
                        <Col span={12}>
                          <DetailField 
                            label="Price" 
                            value={selectedProduct.price}
                            render={(price) => `$${price?.toFixed(2)}`}
                          />
                          <DetailField label="Unit" value={selectedProduct.unit || 'pcs'} />
                        </Col>
                      </Row>
                      {selectedProduct.description && (
                        <DetailField label="Description" value={selectedProduct.description} />
                      )}
                    </DetailSection>

                    <DetailSection title="Inventory Details">
                      <Row gutter={16}>
                        <Col span={8}>
                          <DetailField 
                            label="Current Stock" 
                            value={selectedProduct.currentStock}
                            render={(stock) => (
                              <div>
                                <span className="text-lg font-semibold">{stock}</span>
                                <Tag color={stockStatus.color} className="ml-2">
                                  {stockStatus.text}
                                </Tag>
                              </div>
                            )}
                          />
                        </Col>
                        <Col span={8}>
                          <DetailField label="Reorder Point" value={selectedProduct.reorderPoint} />
                        </Col>
                        <Col span={8}>
                          <DetailField label="Reserved" value={selectedProduct.reserved || 0} />
                        </Col>
                      </Row>
                    </DetailSection>
                  </Col>

                  <Col span={8}>
                    <DetailSection title="Product Images">
                      {selectedProduct.images && selectedProduct.images.length > 0 ? (
                        <Image.PreviewGroup>
                          <div className="grid grid-cols-3 gap-2">
                            {selectedProduct.images.map((url, idx) => {
                              // Handle both server URLs and base64 images
                              let imageUrl = url;
                              if (url.startsWith('/uploads/')) {
                                imageUrl = `http://localhost:3001${url}`;
                              }
                              
                              return (
                                <Image
                                  key={idx}
                                  src={imageUrl}
                                  alt={`${selectedProduct.name} ${idx + 1}`}
                                  width={100}
                                  height={100}
                                  style={{ objectFit: 'cover', borderRadius: 8 }}
                                  preview={{ mask: 'Preview' }}
                                />
                              );
                            })}
                          </div>
                        </Image.PreviewGroup>
                      ) : selectedProduct.image ? (
                        // Backward compat: show legacy single image
                        <Image
                          src={selectedProduct.image.startsWith('/uploads/') ? `http://localhost:3001${selectedProduct.image}` : selectedProduct.image}
                          alt={selectedProduct.name}
                          width="100%"
                          height={192}
                          style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                          <EyeOutlined className="text-4xl text-gray-400" />
                        </div>
                      )}
                    </DetailSection>

                    <DetailSection title="Quick Actions">
                      <Space direction="vertical" className="w-full">
                        <Button 
                          block 
                          icon={<EditOutlined />} 
                          onClick={() => handleEditProduct(selectedProduct)}
                        >
                          Edit Product
                        </Button>
                        <Button 
                          block 
                          icon={<ScanOutlined />}
                          onClick={() => message.info('Barcode generation feature coming soon')}
                        >
                          Generate Barcode
                        </Button>
                        <Button 
                          block 
                          danger 
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Delete Product',
                              content: 'Are you sure you want to delete this product?',
                              onOk: () => handleDeleteProduct(selectedProduct.id),
                            });
                          }}
                        >
                          Delete Product
                        </Button>
                      </Space>
                    </DetailSection>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'vendors',
              label: 'Product vendors',
              children: (
                <Card>
                  <Table
                    dataSource={vendorRows}
                    columns={vendorColumns}
                    pagination={false}
                    rowKey={(r) => r.key}
                    locale={{ emptyText: 'No vendor data for this product' }}
                  />
                </Card>
              ),
            },
            {
              key: 'orders',
              label: 'Order history',
              children: (
                <Card>
                  <Table
                    dataSource={orderItemRows}
                    columns={orderHistoryColumns}
                    pagination={{ pageSize: 5 }}
                    rowKey={(r) => r.key}
                    locale={{ emptyText: 'No order history for this product' }}
                  />
                </Card>
              ),
            },
            {
              key: 'movement',
              label: 'Movement history',
              children: (
                <Card>
                  <div className="text-gray-600">Movement tracking will be available when receiving and adjustment records are implemented.</div>
                </Card>
              ),
            },
          ]}
        />
      </DetailView>
    );
  };

  // Render product list view
  const renderProductList = () => (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Products</h1>
          <p className="text-gray-600">Manage your product inventory</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddProduct}
          size="large"
        >
          Add Product
        </Button>
      </div>

      {/* Filters and Actions */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <Search
            placeholder="Search products by name or SKU..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ maxWidth: 400 }}
            enterButton={<SearchOutlined />}
          />
          
          <Space>
            <Button icon={<ExportOutlined />}>Export</Button>
            <Button icon={<ScanOutlined />}>Scan Barcode</Button>
          </Space>
        </div>
      </Card>

      {/* Products Table */}
      <Card>
        <Table
          dataSource={filteredProducts}
          columns={columns}
          rowSelection={rowSelection}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            className: 'cursor-pointer hover:bg-gray-50'
          })}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} products`,
          }}
          scroll={{ x: 800 }}
          locale={{ emptyText: 'No data found' }}
        />
      </Card>
    </div>
  );

  // Render drawer form
  const renderDrawerForm = () => (
    <RightDrawer
      title={editingProduct ? 'Edit Product' : 'Add New Product'}
      open={drawerVisible}
      onClose={handleDrawerClose}
      loading={drawerLoading}
      width={640}
    >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleDrawerSave}
        id="drawer-form"
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Product Images" name="images">
              {/* File Upload - upload to server and store URLs */}
              <Upload
                listType="picture-card"
                multiple
                fileList={imageFileList}
                maxCount={5}
                beforeUpload={async (file) => {
                  try {
                    // Upload file to server
                    const result = await uploadAPI.uploadImage(file);
                    
                    // Add uploaded file to fileList with server URL
                    const newFile = {
                      uid: file.uid,
                      name: result.originalName || file.name,
                      status: 'done',
                      url: `http://localhost:3001${result.url}`, // Full URL for display
                      serverUrl: result.url, // Relative URL for database
                    };
                    
                    const newFileList = [...imageFileList, newFile];
                    setImageFileList(newFileList);
                    
                    // Update form field with server URLs
                    const serverUrls = newFileList.map(f => f.serverUrl).filter(Boolean);
                    form.setFieldsValue({ images: serverUrls });
                    
                    return false; // Prevent default upload
                  } catch (e) {
                    message.error(`Failed to upload ${file.name}: ${e.message}`);
                    return false;
                  }
                }}
                onRemove={(file) => {
                  // Remove from fileList and update form
                  const newFileList = imageFileList.filter(f => f.uid !== file.uid);
                  setImageFileList(newFileList);
                  
                  const serverUrls = newFileList.map(f => f.serverUrl).filter(Boolean);
                  form.setFieldsValue({ images: serverUrls });
                  
                  // Optionally delete file from server
                  if (file.serverUrl) {
                    const filename = file.serverUrl.split('/').pop();
                    uploadAPI.deleteImage(filename).catch(console.error);
                  }
                }}
              >
                {imageFileList.length >= 8 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Product Name"
              rules={[
                { required: true, message: 'Please enter product name' },
                { whitespace: true, message: 'Name cannot be empty' },
              ]}
            >
              <Input placeholder="Enter product name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="sku"
              label="SKU"
              rules={[
                { required: true, message: 'Please enter SKU' },
                { whitespace: true, message: 'SKU cannot be empty' },
              ]}
            >
              <Input placeholder="Enter SKU" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="categoryId"
              label="Category"
            >
              <Select 
                placeholder="Select category"
                options={categories
                  .filter(cat => cat.id != null && cat.name) // Filter out null/undefined values
                  .map(cat => ({
                    value: cat.id,
                    label: cat.name,
                  }))
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="price"
              label="Price"
              rules={[{ required: true, message: 'Please enter price' }]}
            >
              <InputNumber 
                placeholder="0.00" 
                prefix="$" 
                className="w-full"
                min={0}
                step={0.01}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="currentStock"
              label="Current Stock"
              rules={[{ required: true, message: 'Please enter current stock' }]}
            >
              <InputNumber 
                placeholder="0" 
                className="w-full"
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="reorderPoint"
              label="Reorder Point"
              rules={[{ required: true, message: 'Please enter reorder point' }]}
            >
              <InputNumber 
                placeholder="0" 
                className="w-full"
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="unit"
              label="Unit"
            >
              <Input placeholder="pcs, kg, lbs" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
        >
          <Input.TextArea 
            placeholder="Product description" 
            rows={4}
          />
        </Form.Item>
      </Form>
    </RightDrawer>
  );

  return (
    <div>
      {currentView === 'list' && renderProductList()}
      {currentView === 'detail' && renderProductDetail()}
      {renderDrawerForm()}
    </div>
  );
};

export default Products;
